import api from './api';
import { API_URL } from './environment';

const resolveDocumentId = (documentRef) => {
  if (documentRef == null || documentRef === '') {
    throw new Error('Documento no especificado');
  }

  if (typeof documentRef === 'object') {
    return documentRef.id ?? documentRef.key;
  }

  const value = String(documentRef);

  if (/^\d+$/.test(value)) {
    return value;
  }

  return value.split('/').pop();
};

const createBlobUrl = (data, contentType = 'application/pdf') => {
  const blob = data instanceof Blob ? data : new Blob([data], { type: contentType });

  return URL.createObjectURL(blob);
};

const documentoService = {
  obtenerDocumentos: async () => {
    const response = await api.get('/documentos');
    return response.data;
  },

  obtenerDocumentoPorId: async (id) => {
    const response = await api.get(`/documentos/${id}`);
    return response.data;
  },

  obtenerDocumentosPorDocumentable: async (documentable_id, documentable_type) => {
    const response = await api.get(`/documentos/documentable/${documentable_id}/${documentable_type}`);
    return response.data;
  },

  crearDocumento: async (documento) => {
    const payload = {
      nombre: documento.nombre,
      documentable_id: parseInt(documento.documentable_id, 10),
      documentable_type: documento.documentable_type,
      mime_type: documento.tipo || documento.mime_type || 'application/pdf',
      path: documento.ruta || documento.path || '',
    };

    const response = await api.post('/documentos', payload);
    return response.data;
  },

  actualizarDocumento: async (id, documento) => {
    const response = await api.put(`/documentos/${id}`, documento);
    return response.data;
  },

  eliminarDocumento: async (id) => {
    const response = await api.delete(`/documentos/${id}`);
    return response.data;
  },

  obtenerDocumentosPorTipo: async (documentable_id, documentable_type) => {
    return documentoService.obtenerDocumentosPorDocumentable(documentable_id, documentable_type);
  },

  subirArchivo: async (file, documentable_id, documentable_type, options = {}) => {
    if (!file) {
      throw new Error('No se ha seleccionado ningún archivo');
    }

    if (!documentable_id || !documentable_type) {
      throw new Error('Se requieren documentable_id y documentable_type para subir el archivo');
    }

    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('documentable_id', documentable_id.toString());
    formData.append('documentable_type', documentable_type);

    if (options.usarCarpetaComun) {
      formData.append('usar_carpeta_comun', 'true');
    }

    if (options.carpetaDestino) {
      formData.append('carpeta_destino', options.carpetaDestino);
    }

    const response = await api.post('/documentos/subir', formData);

    if (!response.data) {
      throw new Error('No se recibió respuesta del servidor');
    }

    const documento = response.data.documento || response.data;

    return {
      id: documento.id,
      nombre: documento.nombre || file.name,
      mime_type: documento.mime_type,
      ver_url: documento.ver_url,
      descargar_url: documento.descargar_url,
      key: documento.key || documento.path || null,
      mensaje: response.data.mensaje || 'Archivo subido exitosamente',
    };
  },

  verDocumento: async (documentRef) => {
    const documentId = resolveDocumentId(documentRef);
    const response = await api.get(`/documentos/${documentId}/ver`, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] || 'application/pdf';
    const url = createBlobUrl(response.data, contentType);

    return {
      url,
      blob: response.data,
      contentType,
    };
  },

  descargarDocumento: async (documentRef, nombreArchivo) => {
    const documentId = resolveDocumentId(documentRef);
    const response = await api.get(`/documentos/${documentId}/descargar`, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] || 'application/pdf';
    const url = createBlobUrl(response.data, contentType);

    return {
      url,
      blob: response.data,
      contentType,
      nombre: nombreArchivo || 'documento.pdf',
    };
  },

  getDocumentUrl: (documentRef) => {
    const documentId = resolveDocumentId(documentRef);
    return `${API_URL}/documentos/${documentId}/ver`;
  },

  getDownloadUrl: (documentRef) => {
    const documentId = resolveDocumentId(documentRef);
    return `${API_URL}/documentos/${documentId}/descargar`;
  },
};

export default documentoService;
